import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  UsersIcon, 
  LinkIcon, 
  EyeIcon, 
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const response = await adminService.getDashboard();
      
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      toast.error('Dashboard verileri yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Admin dashboard yükleniyor..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-pink-700 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Admin Panel 🛡️
        </h1>
        <p className="text-red-100">
          Sistem yönetimi ve istatistikleri
        </p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-50">
                <UsersIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Kullanıcı</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {dashboardData?.totalUsers?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-green-600">
                  Aktif: {dashboardData?.activeUsers || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-50">
                <LinkIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Link</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {dashboardData?.totalLinks?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-50">
                <EyeIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Tıklama</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {dashboardData?.totalClicks?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-green-600">
                  Bugün: {dashboardData?.todayStats?.clicks || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-yellow-50">
                <CurrencyDollarIcon className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Kazanç</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${dashboardData?.totalEarnings?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Payments Alert */}
      {dashboardData?.pendingPayments > 0 && (
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="card-body">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-3" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  {dashboardData.pendingPayments} bekleyen ödeme talebi var
                </p>
              </div>
              <Link to="/admin/payments" className="btn btn-warning btn-sm">
                İncele
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Son Aktiviteler</h2>
          </div>
          <div className="card-body">
            {dashboardData?.recentActivity?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recentActivity.slice(0, 8).map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleString('tr-TR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Henüz aktivite bulunmuyor</p>
            )}
          </div>
        </div>

        {/* Top Users */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">En Çok Kazanan Kullanıcılar</h2>
            <Link to="/admin/users" className="btn btn-secondary btn-sm">
              Tümü
              <ArrowRightIcon className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="card-body">
            {dashboardData?.topUsers?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.topUsers.map((user, index) => (
                  <div key={user.email} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 font-medium text-sm">
                          {index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-green-600">
                      ${user.totalEarnings?.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Henüz kullanıcı bulunmuyor</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Links */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">En Popüler Linkler</h2>
            <Link to="/admin/links" className="btn btn-secondary btn-sm">
              Tümü
              <ArrowRightIcon className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="card-body">
            {dashboardData?.topLinks?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.topLinks.map((link, index) => (
                  <div key={link.shortCode} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-medium text-sm">
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {link.title || link.shortCode}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {link.userEmail}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {link.clickCount?.toLocaleString()} tık
                      </p>
                      <p className="text-xs text-green-600">
                        ${link.totalEarnings?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Henüz link bulunmuyor</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Hızlı İşlemler</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4">
              <Link to="/admin/users" className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <UsersIcon className="w-8 h-8 text-blue-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">Kullanıcılar</p>
                <p className="text-xs text-gray-600">Kullanıcı yönetimi</p>
              </Link>

              <Link to="/admin/payments" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <CurrencyDollarIcon className="w-8 h-8 text-green-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">Ödemeler</p>
                <p className="text-xs text-gray-600">Ödeme talepleri</p>
              </Link>

              <Link to="/admin/countries" className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <ChartBarIcon className="w-8 h-8 text-purple-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">CPM Oranları</p>
                <p className="text-xs text-gray-600">Ülke ayarları</p>
              </Link>

              <Link to="/admin/ads" className="p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors">
                <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">Reklamlar</p>
                <p className="text-xs text-gray-600">Reklam yönetimi</p>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Stats */}
      <div className="card bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Günlük İstatistikler</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {dashboardData?.todayStats?.clicks || 0}
              </p>
              <p className="text-sm text-gray-600">Bugün Tıklama</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                ${dashboardData?.todayStats?.earnings?.toFixed(2) || '0.00'}
              </p>
              <p className="text-sm text-gray-600">Bugün Kazanç</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {dashboardData?.thisMonthStats?.clicks || 0}
              </p>
              <p className="text-sm text-gray-600">Bu Ay Tıklama</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">
                ${dashboardData?.thisMonthStats?.earnings?.toFixed(2) || '0.00'}
              </p>
              <p className="text-sm text-gray-600">Bu Ay Kazanç</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;